import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BdoApiService } from '../../../services/bdo-api.service';
import { Ablesung, Zaehlerhistorie, ZaehlerIdentifier, Zaehlerstand } from '../../../../../assets/js/com/ts_api_client';
import { Router } from '@angular/router';
import { TRACKING } from '../../../enums/trackingParts.enum';
import { DivisionToUnitPipe } from '../../../pipes/division-to-unit.pipe';
import { TrackingService } from '../../../services/tracking.service';
import { CustomerStoreService } from '../../../services/customer-store.service';

const CONTEXT_FROM_SIMINVOICE_METER_READING = 'CONTEXT_FROM_SIMINVOICE_METER_READING';
@Component({
    selector: 'bdo-siminvoice-meter-readings',
    templateUrl: './siminvoice-meter-readings.component.html',
    styleUrls: ['./siminvoice-meter-readings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SiminvoiceMeterReadingsComponent implements OnChanges {

  @Input() selectedDate: Date;
  @Input() meter: ZaehlerIdentifier;
  @Input() invalidText: boolean = false;
  @Input() minDateForChanges: Date;
  @Input() maxDateForChanges: Date;
  @Output() validEvent: EventEmitter<Zaehlerstand> = new EventEmitter();
  public meterreading: Zaehlerstand;
  public isExistingMeterreading: boolean = false;
  public alternativeDate: Date | null = null;
  public accountId: string = '';
  public form = new FormGroup({
    firstZaehlwerkValue: new FormControl({
      value: 'Meterreading ERROR',
      disabled: true
    }),
    secondZaehlwerkValue: new FormControl({
      value: 'Meterreading ERROR',
      disabled: true
    })
  });
  private historyCache: Zaehlerhistorie | null = null;

  constructor(
    private readonly customerStoreService: CustomerStoreService,
    public apiService: BdoApiService,
    public router: Router,
    public divisiontoUnitPipe: DivisionToUnitPipe,
    public trackingService: TrackingService) {
  }

  ngOnChanges(changes: SimpleChanges) {
    this.alternativeDate = null;
    this.isExistingMeterreading = false;
    if (changes['meter']) {
      this.historyCache = null;
    }
    if (this.historyCache) {
      this.processHistory(this.historyCache);
    } else {
      this.initFromHistory();
    }
  }

  initFromHistory() {
    this.apiService.getZaehlerStandHistorie(this.meter?.meterNumber, this.meter?.equipment).subscribe({
      next: (history: Zaehlerhistorie) => {
        this.historyCache = history;
        this.processHistory(history);
      },
      error: () => this.validOutput()
    });
  }

  initMeterreadingExists() {
    let temp: string = '';
    if (this.meterreading.zaehlwerkeListe.length === 2) {
      if (this.meterreading.zaehlwerkeListe[1].wert !== null) {
         this.isExistingMeterreading = true;
         temp = ' (HT)';
         this.form.get('secondZaehlwerkValue').setValue(this.meterreading.zaehlwerkeListe[1].wert + ' '
         + this.divisiontoUnitPipe.mapDivision(this.meterreading.sparte) + ' (NT)');
      }
    }
    this.isExistingMeterreading = (this.meterreading.zaehlwerkeListe[0].wert !== null);
    this.form.get('firstZaehlwerkValue').setValue(this.meterreading.zaehlwerkeListe[0].wert + ' '
    + this.divisiontoUnitPipe.mapDivision(this.meterreading.sparte) + temp);

  }

  toMeterreadingsAdd() {
    this.router.navigate(['zaehlerstand/' + this.customerStoreService.getAccountId()], {
      state: {
        context: CONTEXT_FROM_SIMINVOICE_METER_READING,
        date: this.selectedDate,
        meterNumber: this.meter?.meterNumber
      }
    });
    this.trackingService.postSimpleTracking(TRACKING.LOCATION.SIMINVOICE, TRACKING.ACTION.GOTO, TRACKING.LOCATION.METERREADINGS_ADD);
  }

  validOutput() {
    this.validEvent.emit(this.meterreading);
  }

  private processHistory(history: Zaehlerhistorie) {
    const ablesungen = history.ablesungen ?? [];

    const exactMatch = ablesungen.find(a =>
      this.selectedDate && new Date(a.ablesedatum).toDateString() === new Date(this.selectedDate).toDateString()
    );
    if (exactMatch) {
      this.meterreading = this.buildZaehlerstandFromAblesung(exactMatch, history);
      this.alternativeDate = null;
      this.initMeterreadingExists();
      this.validOutput();
      return;
    }

    if (this.minDateForChanges && this.maxDateForChanges) {
      const inRange = ablesungen
        .filter(ablesung => {
          const ablesedatum = new Date(ablesung.ablesedatum);
          return ablesedatum >= this.minDateForChanges && ablesedatum <= this.maxDateForChanges;
        })
        .sort((ablesungA, ablesungB) => new Date(ablesungB.ablesedatum).getTime() - new Date(ablesungA.ablesedatum).getTime());

      if (inRange.length > 0) {
        this.meterreading = this.buildZaehlerstandFromAblesung(inRange[0], history);
        const altDate = new Date(inRange[0].ablesedatum);
        this.alternativeDate = altDate.toDateString() === new Date(this.selectedDate).toDateString() ? null : altDate;
        this.initMeterreadingExists();
        this.validOutput();
        return;
      }
    }

    this.meterreading = { meterNumber: history.meterNumber, sparte: history.sparte, zaehlwerkeListe: [{ wert: null }] };
    this.isExistingMeterreading = false;
    this.validOutput();
  }

  private buildZaehlerstandFromAblesung(ablesung: Ablesung, history: Zaehlerhistorie): Zaehlerstand {
    return {
      meterNumber: history.meterNumber,
      sparte: history.sparte,
      zaehlwerkeListe: (ablesung.staende ?? []).map(ablesungStand => ({
        wert: ablesungStand.stand != null ? parseFloat(ablesungStand.stand.replace(',', '.')) : null,
        einheit: ablesung.einheitStand,
        typ: ablesungStand.typ
      }))
    };
  }

}
